import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Truck, Search, Check, X, AlertTriangle, Upload, Image } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TruckMasterPage = () => {
  const { user } = useAuth();
  const [trucks, setTrucks] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedTruck, setSelectedTruck] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [uploading, setUploading] = useState({ pan: false, rc: false });
  
  const panInputRef = useRef(null);
  const rcInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    truck_number: "",
    truck_type: "",
    capacity: "",
    capacity_unit: "tons",
    manufacturer: "",
    model: "",
    year_of_manufacture: new Date().getFullYear(),
    registration_date: "",
    insurance_expiry: "",
    fitness_expiry: "",
    permit_expiry: "",
    owner_type: "own",
    owner_name: "",
    pan_number: "",
    pan_image_url: "",
    rc_image_url: "",
    vendor_id: "",
    vendor_name: "",
    driver_id: "",
    driver_name: "",
    status: "active"
  });

  const truckTypes = ["Container", "Trailer", "Tanker", "Open Body", "Flatbed", "Tipper", "Refrigerated"];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [trucksRes, vendorsRes, employeesRes] = await Promise.all([
        axios.get(`${API_URL}/trucks`),
        axios.get(`${API_URL}/vendors`),
        axios.get(`${API_URL}/employees`)
      ]);
      setTrucks(trucksRes.data);
      setVendors(vendorsRes.data.filter(v => v.status === "active"));
      setEmployees(employeesRes.data.filter(e => e.designation === "Driver" && e.status === "active"));
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;
    
    setUploading(prev => ({ ...prev, [type]: true }));
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await axios.post(`${API_URL}/upload-image`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      
      if (type === "pan") {
        setFormData(prev => ({ ...prev, pan_image_url: imageUrl }));
      } else {
        setFormData(prev => ({ ...prev, rc_image_url: imageUrl }));
      }
      
      toast.success(`${type.toUpperCase()} image uploaded successfully`);
    } catch (error) {
      toast.error(`Failed to upload ${type.toUpperCase()} image`);
    } finally {
      setUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        capacity: parseFloat(formData.capacity),
        year_of_manufacture: parseInt(formData.year_of_manufacture)
      };

      if (selectedTruck) {
        await axios.put(`${API_URL}/trucks/${selectedTruck.id}`, submitData);
        toast.success("Truck updated successfully");
      } else {
        await axios.post(`${API_URL}/trucks`, submitData);
        toast.success("Truck added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleVerify = async (truckId) => {
    try {
      await axios.post(`${API_URL}/trucks/${truckId}/verify`);
      toast.success("Truck verified successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    }
  };

  const handleReject = async () => {
    try {
      await axios.post(`${API_URL}/trucks/${selectedTruck.id}/reject?reason=${encodeURIComponent(rejectReason)}`);
      toast.success("Truck rejected");
      setRejectDialogOpen(false);
      setSelectedTruck(null);
      setRejectReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Rejection failed");
    }
  };

  const handleEdit = (truck) => {
    setSelectedTruck(truck);
    setFormData({
      truck_number: truck.truck_number,
      truck_type: truck.truck_type,
      capacity: truck.capacity.toString(),
      capacity_unit: truck.capacity_unit,
      manufacturer: truck.manufacturer,
      model: truck.model,
      year_of_manufacture: truck.year_of_manufacture,
      registration_date: truck.registration_date,
      insurance_expiry: truck.insurance_expiry,
      fitness_expiry: truck.fitness_expiry,
      permit_expiry: truck.permit_expiry,
      owner_type: truck.owner_type,
      owner_name: truck.owner_name || "",
      pan_number: truck.pan_number || "",
      pan_image_url: truck.pan_image_url || "",
      rc_image_url: truck.rc_image_url || "",
      vendor_id: truck.vendor_id || "",
      vendor_name: truck.vendor_name || "",
      driver_id: truck.driver_id || "",
      driver_name: truck.driver_name || "",
      status: truck.status
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/trucks/${selectedTruck.id}`);
      toast.success("Truck deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedTruck(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  const resetForm = () => {
    setSelectedTruck(null);
    setFormData({
      truck_number: "",
      truck_type: "",
      capacity: "",
      capacity_unit: "tons",
      manufacturer: "",
      model: "",
      year_of_manufacture: new Date().getFullYear(),
      registration_date: "",
      insurance_expiry: "",
      fitness_expiry: "",
      permit_expiry: "",
      owner_type: "own",
      owner_name: "",
      pan_number: "",
      pan_image_url: "",
      rc_image_url: "",
      vendor_id: "",
      vendor_name: "",
      driver_id: "",
      driver_name: "",
      status: "active"
    });
  };

  const handleVendorChange = (vendorId) => {
    const vendor = vendors.find(v => v.id === vendorId);
    setFormData({
      ...formData,
      vendor_id: vendorId,
      vendor_name: vendor?.name || ""
    });
  };

  const handleDriverChange = (driverId) => {
    const driver = employees.find(e => e.id === driverId);
    setFormData({
      ...formData,
      driver_id: driverId,
      driver_name: driver?.name || ""
    });
  };

  const getVerificationBadge = (status) => {
    const styles = {
      pending: "border-yellow-500/50 text-yellow-400 bg-yellow-500/10",
      verified: "border-green-500/50 text-green-400 bg-green-500/10",
      rejected: "border-red-500/50 text-red-400 bg-red-500/10"
    };
    return styles[status] || styles.pending;
  };

  const canVerify = user?.role === "admin" || user?.role === "super_admin";
  const canReject = user?.role === "admin" || user?.role === "super_admin";
  const canDelete = user?.role === "admin" || user?.role === "super_admin";

  const filteredTrucks = trucks.filter(truck =>
    truck.truck_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.truck_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
    truck.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-primary animate-pulse font-heading text-xl">Loading trucks...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="truck-master-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Truck className="w-8 h-8 text-primary" />
            Truck Master
          </h1>
          <p className="text-muted-foreground mt-1">Manage fleet vehicles and their documents</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="glow-primary bg-primary hover:bg-primary/90"
          data-testid="add-truck-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Truck
        </Button>
      </div>

      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by truck number, type, or manufacturer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-secondary/50 border-border"
              data-testid="truck-search-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border bg-card">
        <CardHeader className="border-b border-border/50">
          <CardTitle className="font-heading">All Trucks ({filteredTrucks.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Truck Number</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Type</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Capacity</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Owner</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Driver</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Verification</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider">Status</TableHead>
                  <TableHead className="text-muted-foreground uppercase text-xs tracking-wider text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTrucks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No trucks found. Add your first truck to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTrucks.map((truck) => (
                    <TableRow key={truck.id} className="border-border table-row-hover" data-testid={`truck-row-${truck.id}`}>
                      <TableCell className="font-mono font-bold text-primary">{truck.truck_number}</TableCell>
                      <TableCell className="text-muted-foreground">{truck.truck_type}</TableCell>
                      <TableCell className="text-muted-foreground">{truck.capacity} {truck.capacity_unit}</TableCell>
                      <TableCell className="text-muted-foreground capitalize">{truck.owner_type}</TableCell>
                      <TableCell className="text-muted-foreground">{truck.driver_name || "-"}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getVerificationBadge(truck.verification_status)}>
                          {truck.verification_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={truck.status === "active" 
                          ? "border-green-500/50 text-green-400 bg-green-500/10" 
                          : "border-red-500/50 text-red-400 bg-red-500/10"
                        }>
                          {truck.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {canVerify && truck.verification_status === "pending" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-400 hover:text-green-300"
                              onClick={() => handleVerify(truck.id)}
                              data-testid={`verify-truck-${truck.id}`}
                              title="Verify"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                          {canReject && truck.verification_status === "pending" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-400 hover:text-red-300"
                              onClick={() => { setSelectedTruck(truck); setRejectDialogOpen(true); }}
                              data-testid={`reject-truck-${truck.id}`}
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(truck)}
                            data-testid={`edit-truck-${truck.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => { setSelectedTruck(truck); setDeleteDialogOpen(true); }}
                              data-testid={`delete-truck-${truck.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedTruck ? "Edit Truck" : "Add New Truck"}
            </DialogTitle>
            <DialogDescription>
              {selectedTruck ? "Update truck details" : "Enter the details of the new truck"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="truck_number">Truck Number</Label>
                <Input
                  id="truck_number"
                  value={formData.truck_number}
                  onChange={(e) => setFormData({ ...formData, truck_number: e.target.value.toUpperCase() })}
                  className="bg-secondary/50 border-border font-mono"
                  placeholder="MH-12-AB-1234"
                  required
                  disabled={!!selectedTruck}
                  data-testid="truck-number-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="truck_type">Truck Type</Label>
                <Select value={formData.truck_type} onValueChange={(v) => setFormData({ ...formData, truck_type: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="truck-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {truckTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity</Label>
                <Input
                  id="capacity"
                  type="number"
                  step="0.1"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="20"
                  required
                  data-testid="truck-capacity-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capacity_unit">Unit</Label>
                <Select value={formData.capacity_unit} onValueChange={(v) => setFormData({ ...formData, capacity_unit: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tons">Tons</SelectItem>
                    <SelectItem value="kg">Kg</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner_type">Owner Type</Label>
                <Select value={formData.owner_type} onValueChange={(v) => setFormData({ ...formData, owner_type: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="truck-owner-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="own">Own</SelectItem>
                    <SelectItem value="attached">Attached</SelectItem>
                    <SelectItem value="hired">Hired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="manufacturer">Manufacturer</Label>
                <Input
                  id="manufacturer"
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="Tata"
                  required
                  data-testid="truck-manufacturer-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="bg-secondary/50 border-border"
                  placeholder="Prima"
                  required
                  data-testid="truck-model-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="year_of_manufacture">Year</Label>
                <Input
                  id="year_of_manufacture"
                  type="number"
                  value={formData.year_of_manufacture}
                  onChange={(e) => setFormData({ ...formData, year_of_manufacture: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="truck-year-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="registration_date">Registration Date</Label>
                <Input
                  id="registration_date"
                  type="date"
                  value={formData.registration_date}
                  onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="truck-registration-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="insurance_expiry">Insurance Expiry</Label>
                <Input
                  id="insurance_expiry"
                  type="date"
                  value={formData.insurance_expiry}
                  onChange={(e) => setFormData({ ...formData, insurance_expiry: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="truck-insurance-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="fitness_expiry">Fitness Expiry</Label>
                <Input
                  id="fitness_expiry"
                  type="date"
                  value={formData.fitness_expiry}
                  onChange={(e) => setFormData({ ...formData, fitness_expiry: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="truck-fitness-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="permit_expiry">Permit Expiry</Label>
                <Input
                  id="permit_expiry"
                  type="date"
                  value={formData.permit_expiry}
                  onChange={(e) => setFormData({ ...formData, permit_expiry: e.target.value })}
                  className="bg-secondary/50 border-border"
                  required
                  data-testid="truck-permit-input"
                />
              </div>
            </div>

            {formData.owner_type !== "own" && (
              <div className="space-y-2">
                <Label htmlFor="vendor">Vendor</Label>
                <Select value={formData.vendor_id} onValueChange={handleVendorChange}>
                  <SelectTrigger className="bg-secondary/50 border-border" data-testid="truck-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Owner Details Section */}
            <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20">
              <h3 className="font-semibold text-primary">Owner/Broker Details</h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="owner_name">Owner Name</Label>
                  <Input
                    id="owner_name"
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    className="bg-secondary/50 border-border"
                    placeholder="Vehicle Owner/Broker Name"
                    data-testid="truck-owner-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pan_number">PAN Number</Label>
                  <Input
                    id="pan_number"
                    value={formData.pan_number}
                    onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                    className="bg-secondary/50 border-border font-mono"
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    data-testid="truck-pan-number-input"
                  />
                </div>
              </div>
            </div>

            {/* Vehicle Documents Section */}
            <div className="border border-border rounded-lg p-4 space-y-4 bg-secondary/20">
              <h3 className="font-semibold text-primary flex items-center gap-2">
                <Image className="w-4 h-4" />
                Vehicle Documents (for Load Slips)
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                {/* PAN Card Upload */}
                <div className="space-y-2">
                  <Label>PAN Card Image</Label>
                  <input
                    type="file"
                    accept="image/*"
                    ref={panInputRef}
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], "pan")}
                  />
                  {formData.pan_image_url ? (
                    <div className="relative border border-border rounded-lg p-2 bg-secondary/30">
                      <img 
                        src={formData.pan_image_url} 
                        alt="PAN Card" 
                        className="w-full h-24 object-cover rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6"
                        onClick={() => setFormData({ ...formData, pan_image_url: "" })}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-24 border-dashed"
                      onClick={() => panInputRef.current?.click()}
                      disabled={uploading.pan}
                      data-testid="upload-truck-pan-btn"
                    >
                      {uploading.pan ? (
                        <span className="animate-pulse">Uploading...</span>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <Upload className="w-5 h-5" />
                          <span className="text-xs">Upload PAN</span>
                        </div>
                      )}
                    </Button>
                  )}
                </div>

                {/* RC Upload */}
                <div className="space-y-2">
                  <Label>Vehicle RC Image</Label>
                  <input
                    type="file"
                    accept="image/*"
                    ref={rcInputRef}
                    className="hidden"
                    onChange={(e) => handleImageUpload(e.target.files[0], "rc")}
                  />
                  {formData.rc_image_url ? (
                    <div className="relative border border-border rounded-lg p-2 bg-secondary/30">
                      <img 
                        src={formData.rc_image_url} 
                        alt="Vehicle RC" 
                        className="w-full h-24 object-cover rounded"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        className="absolute top-1 right-1 h-6 w-6"
                        onClick={() => setFormData({ ...formData, rc_image_url: "" })}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full h-24 border-dashed"
                      onClick={() => rcInputRef.current?.click()}
                      disabled={uploading.rc}
                      data-testid="upload-truck-rc-btn"
                    >
                      {uploading.rc ? (
                        <span className="animate-pulse">Uploading...</span>
                      ) : (
                        <div className="flex flex-col items-center gap-1">
                          <Upload className="w-5 h-5" />
                          <span className="text-xs">Upload RC</span>
                        </div>
                      )}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">These documents will auto-fill when creating Load Slips with this vehicle number.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="driver">Assigned Driver (Optional)</Label>
              <Select value={formData.driver_id} onValueChange={handleDriverChange}>
                <SelectTrigger className="bg-secondary/50 border-border" data-testid="truck-driver-select">
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>{driver.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTruck && (
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-secondary/50 border-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="glow-primary bg-primary" data-testid="truck-submit-btn">
                {selectedTruck ? "Update" : "Add Truck"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              Reject Truck
            </DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting truck {selectedTruck?.truck_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reject_reason">Rejection Reason</Label>
              <Input
                id="reject_reason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="bg-secondary/50 border-border"
                placeholder="Enter reason for rejection..."
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-truck">
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Delete Truck</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete truck "{selectedTruck?.truck_number}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-truck">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TruckMasterPage;
